import React, { useState } from "react";
import axios from "axios";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import SiteFooter from "../components/SiteFooter";
import { CityAutocomplete } from "../components/CityAutocomplete";
import { useLang } from "../i18n/LanguageContext";

const Global = createGlobalStyle`
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #FBF6EA; font-family: 'Pretendard','Malgun Gothic','Apple SD Gothic Neo',Arial,sans-serif; }
`;

const API = process.env.REACT_APP_API_URL || "";

const SERVICE_TYPES = [
  "종합운세", "신년운세", "연애운", "결혼운",
  "직업운", "사업운", "건강운", "연애궁합", "심층궁합",
];

/* ─── keyframes ─── */
const floatV = keyframes`
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(-10px); }
`;

/* ─── layout ─── */
const DancheongBar = styled.div`
  height: 6px;
  width: 100%;
  background: #FBF6EA;
`;

const PageWrap = styled.div`
  max-width: 520px;
  margin: 0 auto;
  padding: 0 16px 80px;
  background: #FBF6EA;
`;

/* 전체 씬 히어로 */
const HeroWrap = styled.div`
  width: calc(100% + 32px);
  margin-left: -16px;
  position: relative;
  overflow: hidden;
  margin-bottom: 24px;
`;
const HeroImg = styled.img`
  width: 120px; height: 120px; margin: 0 auto;
  display: block;
  object-fit: contain;
`;
const HeroOverlay = styled.div`
  position: absolute; bottom: 0; left: 0; right: 0; height: 120px;
  background: linear-gradient(to bottom, transparent, #FBF6EA);
`;
const HeroTitle = styled.h1`
  position: absolute; bottom: 22px; left: 0; right: 0;
  text-align: center;
  font-size: 1.5rem; font-weight: 900; color: #2B2A26;
  text-shadow: 0 2px 8px rgba(255,251,240,0.8);
`;
const HeroSub = styled.p`
  position: absolute; bottom: 4px; left: 0; right: 0;
  text-align: center;
  font-size: 0.78rem; color: #7A5C42; font-weight: 600;
  text-shadow: 0 1px 4px rgba(255,251,240,0.9);
`;

const FormCard = styled.div`
  background: #fff;
  border-radius: 20px;
  border: 2px solid #2B2A26;
  padding: 24px 20px;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const CompatBand = styled.div`
  margin-bottom: 14px;
  padding: 16px;
  background: #FFF5F8;
  border: 1.5px solid #2B2A26;
  border-radius: 14px;
`;
const CompatBandTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  color: #D0654F;
  margin-bottom: 14px;
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 15px;
  background: #B8862A;
  color: #fff;
  border: 2px solid #2B2A26;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 800;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  letter-spacing: -0.02em;
  &:hover { opacity: 0.92; transform: translateY(-1px); }
  &:active { transform: translateY(0); }
  &:disabled { background: #ccc; cursor: not-allowed; }
`;

const ErrBox = styled.div`
  padding: 10px 14px;
  background: #FBE0D9;
  border: 1px solid #2B2A26;
  border-radius: 10px;
  font-size: 0.82rem;
  color: #c92a2a;
  margin-bottom: 14px;
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;
const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.8);
  border: 1.5px solid #2B2A26;
  border-radius: 20px;
  padding: 7px 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  color: #8C8677;
  font-family: inherit;
`;
const PageNameTag = styled.div`
  flex: 1;
  text-align: right;
  font-size: 15px;
  font-weight: 900;
  color: #2B2A26;
`;

const InfoBox = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  background: #FFF8EE;
  border-radius: 14px;
  border: 1px solid #2B2A26;
  font-size: 0.78rem;
  color: #8C8677;
  line-height: 1.8;
`;

/* ─── 인라인 스타일 → styled 대체용 ─── */
const inputStyle: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", padding: "10px 12px", border: "1.5px solid #2B2A26",
  borderRadius: 10, fontSize: "0.9rem", color: "#2B2A26",
  background: "#FBF6EA", fontFamily: "inherit", marginBottom: 14,
};
const inputDisabledStyle: React.CSSProperties = {
  ...inputStyle, background: "#FBF6EA", color: "#8C8677", cursor: "not-allowed",
};
const labelStyle: React.CSSProperties = {
  display: "block", fontSize: 12, fontWeight: 600, color: "#8C8677", marginBottom: 6,
};

function parseTime(val: string) {
  const s = val.replace(/\s/g, "");
  if (!s) return { hour: 99, minute: 0 };
  let h: number, m: number;
  if (s.includes(":")) {
    const parts = s.split(":");
    h = parseInt(parts[0]); m = parseInt(parts[1] || "0");
  } else if (/^\d{3,4}$/.test(s)) {
    const p = s.padStart(4, "0");
    h = parseInt(p.slice(0, 2)); m = parseInt(p.slice(2));
  } else {
    h = parseInt(s); m = 0;
  }
  return {
    hour: (!isNaN(h) && h >= 0 && h <= 23) ? h : 99,
    minute: (!isNaN(m) && m >= 0 && m <= 59) ? m : 0,
  };
}

function parseBirthDate(val: string) {
  const sep = val.includes("/") ? "/" : val.includes(".") ? "." : val.includes("-") ? "-" : null;
  if (sep) {
    const [y, m, d] = val.split(sep).map(Number);
    return { year: y || 0, month: m || 0, day: d || 0 };
  }
  if (val.length === 8) {
    return { year: Number(val.slice(0,4)), month: Number(val.slice(4,6)), day: Number(val.slice(6,8)) };
  }
  return { year: 0, month: 0, day: 0 };
}

const COMPAT_SERVICES = ["연애궁합", "심층궁합", "재회운", "결혼운"];

const emptyForm = () => ({
  name: "", email: "", phone: "", gender: "female",
  birth_date: "", birth_type: "solar",
  birth_time: "", birth_time_unknown: false,
  city: "서울특별시",
  service_type: "종합운세", memo: "", consent: false,
});

const emptyPartner = () => ({
  name: "", gender: "female",
  birth_date: "", birth_type: "solar",
  birth_time: "", birth_time_unknown: false,
  city: "서울특별시",
});

export default function VisitPage() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm());
  const [partner, setPartner] = useState(emptyPartner());
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [done,    setDone]    = useState(false);

  const isCompat = COMPAT_SERVICES.includes(form.service_type);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));
  const setP = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setPartner(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.consent) { setError(t("visit.errConsent")); return; }
    if (!form.name || !form.birth_date) {
      setError(t("visit.errRequired")); return;
    }
    const { year, month, day } = parseBirthDate(form.birth_date);
    if (!year || !month || !day) {
      setError(t("visit.errBirthFormat")); return;
    }
    if (isCompat) {
      const pd = parseBirthDate(partner.birth_date);
      if (!partner.name || !pd.year) {
        setError(t("visit.errPartner")); return;
      }
    }
    setLoading(true); setError("");
    const pd = parseBirthDate(partner.birth_date);
    const partnerPayload = isCompat ? {
      name: partner.name,
      gender: partner.gender,
      birth_year: pd.year, birth_month: pd.month, birth_day: pd.day,
      birth_hour: partner.birth_time_unknown ? 99 : parseTime(partner.birth_time).hour,
      birth_minute: partner.birth_time_unknown ? 0 : parseTime(partner.birth_time).minute,
      birth_type: partner.birth_type,
      city: partner.city,
      compatibility_type: form.service_type === "심층궁합" ? "deep" : "casual",
    } : null;
    try {
      await axios.post(`${API}/api/v1/visit/submit`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        gender: form.gender,
        birth_year: year, birth_month: month, birth_day: day,
        birth_hour: form.birth_time_unknown ? 99 : parseTime(form.birth_time).hour,
        birth_minute: form.birth_time_unknown ? 0 : parseTime(form.birth_time).minute,
        birth_type: form.birth_type,
        city: form.city,
        service_type: form.service_type,
        partner_info: partnerPayload,
        memo: form.memo,
        consent: true,
        lang,
      });
      setDone(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || t("visit.errSubmit"));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <>
        <Global />
        <DancheongBar />
        <PageWrap>
          <HeroWrap>
            <HeroImg src="/woo_y_2.png" alt="우연" />
            <HeroTitle>{t("visit.doneBrand")}</HeroTitle>
          </HeroWrap>
          <FormCard style={{ textAlign: "center" }}>
            <div style={{ fontSize: "2.4rem", marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: "1.1rem", fontWeight: 900, color: "#2B2A26", marginBottom: 10 }}>
              {t("visit.doneTitle")}
            </div>
            <div style={{ fontSize: "0.85rem", color: "#8C8677", lineHeight: 1.8 }}>
              {t("visit.doneDesc")}<br />
              {form.email && <>{t("visit.doneEmail", { email: form.email })}<br /></>}
              {t("visit.doneContact")}
            </div>
            <SubmitBtn
              onClick={() => { setDone(false); setForm(emptyForm()); setPartner(emptyPartner()); }}
              style={{ marginTop: 20 }}
            >
              {t("visit.doneNewBtn")}
            </SubmitBtn>
          </FormCard>
        </PageWrap>
      </>
    );
  }

  return (
    <>
      <Global />
      <DancheongBar />
      <PageWrap>
        <NavRow>
          <BackBtn onClick={() => navigate("/")}>{t("visit.backHome")}</BackBtn>
          <PageNameTag>{t("visit.title")}</PageNameTag>
        </NavRow>

        <HeroWrap>
          <HeroImg src="/woo_y_2.png" alt="우연이와 1:1 방문 상담" />
          <HeroOverlay />
          <HeroTitle>{t("visit.title")}</HeroTitle>
          <HeroSub>{t("visit.heroSub")}</HeroSub>
        </HeroWrap>

        <FormCard>
          <form onSubmit={handleSubmit}>
            {/* 이름 + 성별 */}
            <TwoCol>
              <div>
                <label style={labelStyle}>{t("visit.nameLabel")}</label>
                <input style={inputStyle} value={form.name} onChange={set("name")} placeholder={t("visit.namePlaceholder")} required />
              </div>
              <div>
                <label style={labelStyle}>{t("visit.genderLabel")}</label>
                <select style={inputStyle} value={form.gender} onChange={set("gender")}>
                  <option value="female">{t("common.female")}</option>
                  <option value="male">{t("common.male")}</option>
                </select>
              </div>
            </TwoCol>

            {/* 생년월일 — 한 칸 */}
            <label style={labelStyle}>{t("visit.birthDateLabel")}</label>
            <input style={inputStyle} value={form.birth_date} onChange={set("birth_date")} placeholder="1990/05/15" maxLength={10} />

            {/* 양음력 */}
            <label style={labelStyle}>{t("visit.birthTypeLabel")}</label>
            <select style={inputStyle} value={form.birth_type} onChange={set("birth_type")}>
              <option value="solar">{t("visit.solar")}</option>
              <option value="lunar">{t("visit.lunar")}</option>
              <option value="lunar_leap">{t("visit.lunarLeap")}</option>
            </select>

            {/* 태어난 시간 */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>{t("visit.birthTimeLabel")}</label>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "#8C8677", cursor: "pointer" }}>
                <input type="checkbox" checked={form.birth_time_unknown}
                  onChange={e => setForm(f => ({ ...f, birth_time_unknown: e.target.checked }))}
                  style={{ accentColor: "#B8862A" }} />
                {t("common.timeUnknown")}
              </label>
            </div>
            <input
              style={form.birth_time_unknown ? inputDisabledStyle : inputStyle}
              placeholder={t("visit.timePlaceholder")}
              maxLength={5}
              value={form.birth_time_unknown ? "" : form.birth_time}
              disabled={form.birth_time_unknown}
              onChange={set("birth_time")} />

            {/* 출생 도시 */}
            <label style={labelStyle}>{t("visit.cityLabel")}</label>
            <div style={{ marginBottom: 14 }}>
              <CityAutocomplete value={form.city} onChange={v => setForm(f => ({ ...f, city: v }))} />
            </div>

            {/* 이메일 + 전화 */}
            <label style={labelStyle}>{t("visit.emailLabel")}</label>
            <input style={inputStyle} type="email" value={form.email} onChange={set("email")} placeholder="example@email.com" />
            <label style={labelStyle}>{t("visit.phoneLabel")}</label>
            <input style={inputStyle} value={form.phone} onChange={set("phone")} placeholder="010-0000-0000" />

            {/* 서비스 종류 */}
            <label style={labelStyle}>{t("visit.serviceLabel")}</label>
            <select style={inputStyle} value={form.service_type} onChange={set("service_type")}>
              {SERVICE_TYPES.map(s => <option key={s} value={s}>{t(`svc.${s}`)}</option>)}
            </select>

            {/* 상대방 정보 (궁합 서비스 선택 시) */}
            {isCompat && (
              <CompatBand>
                <CompatBandTitle>{t("visit.compatTitle")}</CompatBandTitle>
                <TwoCol>
                  <div>
                    <label style={labelStyle}>{t("visit.partnerNameLabel")}</label>
                    <input style={inputStyle} value={partner.name} onChange={setP("name")} placeholder={t("visit.namePlaceholder")} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t("visit.partnerGenderLabel")}</label>
                    <select style={inputStyle} value={partner.gender} onChange={setP("gender")}>
                      <option value="female">{t("common.female")}</option>
                      <option value="male">{t("common.male")}</option>
                    </select>
                  </div>
                </TwoCol>
                <label style={labelStyle}>{t("visit.partnerBirthDateLabel")}</label>
                <input style={inputStyle} value={partner.birth_date} onChange={setP("birth_date")} placeholder="1990/05/15" maxLength={10} />
                <label style={labelStyle}>{t("visit.partnerBirthTypeLabel")}</label>
                <select style={inputStyle} value={partner.birth_type} onChange={setP("birth_type")}>
                  <option value="solar">{t("visit.solar")}</option>
                  <option value="lunar">{t("visit.lunar")}</option>
                  <option value="lunar_leap">{t("visit.lunarLeap")}</option>
                </select>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>{t("visit.partnerBirthTimeLabel")}</label>
                  <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "#8C8677", cursor: "pointer" }}>
                    <input type="checkbox" checked={partner.birth_time_unknown}
                      onChange={e => setPartner(p => ({ ...p, birth_time_unknown: e.target.checked }))}
                      style={{ accentColor: "#B8862A" }} />
                    {t("common.timeUnknown")}
                  </label>
                </div>
                <input
                  style={partner.birth_time_unknown ? inputDisabledStyle : inputStyle}
                  placeholder={t("visit.timePlaceholder")}
                  maxLength={5}
                  value={partner.birth_time_unknown ? "" : partner.birth_time}
                  disabled={partner.birth_time_unknown}
                  onChange={setP("birth_time")} />
                <label style={labelStyle}>{t("visit.partnerCityLabel")}</label>
                <div style={{ marginBottom: 14 }}>
                  <CityAutocomplete value={partner.city} onChange={v => setPartner(p => ({ ...p, city: v }))} />
                </div>
              </CompatBand>
            )}

            {/* 메모 */}
            <label style={labelStyle}>{t("visit.memoLabel")}</label>
            <textarea style={{ ...inputStyle, height: 80, resize: "vertical" } as React.CSSProperties}
              value={form.memo} onChange={set("memo")}
              placeholder={t("visit.memoPlaceholder")} />

            {/* 동의 */}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: "0.78rem", color: "#8C8677", marginBottom: 20, cursor: "pointer" }}>
              <input type="checkbox" checked={form.consent}
                onChange={e => setForm(f => ({ ...f, consent: e.target.checked }))}
                style={{ marginTop: 2, accentColor: "#B8862A" }} />
              <span>{t("visit.consentText")}</span>
            </label>

            {error && <ErrBox>{error}</ErrBox>}

            <SubmitBtn type="submit" disabled={loading}>
              {loading ? t("visit.submitLoading") : t("visit.submitBtn")}
            </SubmitBtn>
          </form>
        </FormCard>

        <SiteFooter />
      </PageWrap>
    </>
  );
}
